import React from 'react'
import { mdx } from '@mdx-js/react'
/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Learn how to troubleshoot common errors associated with the Plaid Link flow'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Checkbox = makeShortcode("Checkbox");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Link errors and troubleshooting' subtitle='Errors in the Link flow' mdxType="Header" />
    <h4 {...{
      "id": "link-error-codes"
    }}>{`Link error codes`}</h4>
    <p>{`Any errors during Link will be returned via the Link flow; for details, see the guide for your specific platform. Since any failed user attempt to log on to a financial institution for any reason, even an incorrect password, will result in a Link error, you should expect to see the occasional error during Link. In most cases, Link will guide your user through the steps required to resolve the error.`}</p>
    <p>{`The most common case in which you will need a special flow for Link errors is the `}<inlineCode parentName="p">{`ITEM_LOGIN_REQUIRED`}</inlineCode>{` error. For instructions on handling this error, see `}<a parentName="p" {...{
        "href": "/docs/link/update-mode"
      }}>{`Update mode`}</a>{`.`}</p>
    <p>{`For more details, or if you need to troubleshoot a problem involving an error message from the API, see `}<a parentName="p" {...{
        "href": "/docs/errors/item"
      }}>{`Item errors`}</a>{`.`}</p>
    <h4 {...{
      "id": "missing-institutions-or-connectivity-not-supported-error"
    }}>{`Missing institutions or "Connectivity not supported" error`}</h4>
    <h5 {...{
      "id": "symptom"
    }}>{`Symptom`}</h5>
    <ul>
      <li parentName="ul">{`Your user cannot find their financial institution in Link, even though it is one of the 11,500+ institutions supported by Plaid.`}</li>
      <li parentName="ul">{`Your user experiences a "Connectivity not supported" error message after selecting their institution in Link.`}</li>
    </ul>
    <h5 {...{
      "id": "common-causes"
    }}>{`Common causes`}</h5>
    <ul>
      <li parentName="ul">{`The institution does not support one of the products specified in Link initialization.`}</li>
      <li parentName="ul">{`The institution is associated with a country not specified in Link initialization.`}</li>
      <li parentName="ul">{`The institution is associated with a country your Plaid account hasn't been enabled for.`}</li>
    </ul>
    <h5 {...{
      "id": "troubleshooting-steps"
    }}>{`Troubleshooting steps`}</h5>
    <Checkbox mdxType="Checkbox">
      <p>{`Make sure not to initialize Link with any products your application isn't using. For more details on which products to initialize Link with, see `}<a parentName="p" {...{
          "href": "/docs/link/initializing-products/"
        }}>{`Choosing how to initialize products`}</a>{`.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
  Make sure Link is initialized for the country in which the institution
  operates.
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`If the problem occurs in Production but not in Sandbox, your Plaid developer account may not have been enabled for the
country in which the institution operates. Submit a `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/support/new/admin/account-administration/request-product-access"
        }}>{`product access
request`}</a>{`
via the Plaid Dashboard to gain access.`}</p>
    </Checkbox>
    <h4 {...{
      "id": "institution-unhealthy"
    }}>{`Institution unhealthy`}</h4>
    <h5 {...{
      "id": "symptom-1"
    }}>{`Symptom`}</h5>
    <p>{`When a user tries to connect their account, they receive a message in Link that Plaid may be experiencing connectivity issues to that institution. For more details, see `}<a parentName="p" {...{
        "href": "/docs/link/institution-status/"
      }}>{`Institution status in Link`}</a>{`.`}</p>
    <h5 {...{
      "id": "common-causes-1"
    }}>{`Common causes`}</h5>
    <ul>
      <li parentName="ul">{`Plaid's connection to the institution is down or degraded.`}</li>
    </ul>
    <h5 {...{
      "id": "troubleshooting-steps-1"
    }}>{`Troubleshooting steps`}</h5>
    <Checkbox mdxType="Checkbox">
      <p>{`Enroll in `}<a parentName="p" {...{
          "href": "/docs/link/link-recovery/"
        }}>{`Link Recovery (beta)`}</a>{`. Link Recovery allows your end users to sign up for automatic notifications from Plaid when the connection issue is resolved so that they can retry connecting their account. `}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`If your user cannot link their account, ask them to try again later.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`If possible, provide a manual, backup flow for the user to enter account information.`}</p>
    </Checkbox>
    <h4 {...{
      "id": "duplicate-items"
    }}>{`Duplicate Items`}</h4>
    <h5 {...{
      "id": "symptom-2"
    }}>{`Symptom`}</h5>
    <p>{`Your user has multiple Items that seem to represent the same underlying set of accounts.`}</p>
    <h5 {...{
      "id": "common-causes-2"
    }}>{`Common causes`}</h5>
    <ul>
      <li parentName="ul">{`The user added the same Item more than once.`}</li>
    </ul>
    <h5 {...{
      "id": "troubleshooting-steps-2"
    }}>{`Troubleshooting steps`}</h5>
    <Checkbox mdxType="Checkbox">
      <p>{`See `}<a parentName="p" {...{
          "href": "/docs/link/duplicate-items"
        }}>{`Preventing duplicate Items`}</a>{` for instructions on preventing this scenario.`}</p>
    </Checkbox>
    <h4 {...{
      "id": "missing-accounts"
    }}>{`Missing accounts`}</h4>
    <h5 {...{
      "id": "symptom-3"
    }}>{`Symptom`}</h5>
    <p>{`Some of the end users' accounts are not present in the Item, or a new account added by the user does not appear in the Item.`}</p>
    <h5 {...{
      "id": "common-causes-3"
    }}>{`Common causes`}</h5>
    <ul>
      <li parentName="ul">{`The user linked their account but did not grant your app permission to access the account, or created the account after linking the Item and did not grant your app permission to access newly created accounts.`}</li>
      <li parentName="ul">{`The account is not compatible with settings specified during the `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{` call. For example, if `}<inlineCode parentName="li">{`auth`}</inlineCode>{` was one of the required products specified when calling `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{`, accounts that do not support Auth, such as credit card accounts, will not appear on the Item.`}</li>
    </ul>
    <h5 {...{
      "id": "troubleshooting-steps-3"
    }}>{`Troubleshooting steps`}</h5>
    <Checkbox mdxType="Checkbox">
      <p>{`Prompt the user to enter the `}<a parentName="p" {...{
          "href": "/docs/link/update-mode"
        }}>{`update mode flow`}</a>{`, which will allow them to change the permissions they have granted to your app.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Ensure that the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` call is configured in a way that is compatible with all the desired accounts.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`If accounts disappeared after the Item was linked, see the Troubleshooting guide for `}<a parentName="p" {...{
          "href": "/docs/errors/invalid-input/#invalid_account_id"
        }}><inlineCode parentName="a">{`INVALID_ACCOUNT_ID`}</inlineCode></a>{`.`}</p>
    </Checkbox>
    <h4 {...{
      "id": "link-flow-is-failing"
    }}>{`Link flow is failing`}</h4>
    <h5 {...{
      "id": "symptom-4"
    }}>{`Symptom`}</h5>
    <p>{`The Link flow is not working correctly for the user -- the `}<inlineCode parentName="p">{`onSuccess`}</inlineCode>{` callback may not be sent, Link may not load, or redirects may not function correctly.`}</p>
    <h5 {...{
      "id": "common-causes-4"
    }}>{`Common causes`}</h5>
    <ul>
      <li parentName="ul">{`Third party software, such as ad-blocking browser extensions, may be interfering with Link.`}</li>
      <li parentName="ul">{`The end user linked their account via an OAuth flow and OAuth is not configured correctly.`}</li>
    </ul>
    <h5 {...{
      "id": "troubleshooting-steps-4"
    }}>{`Troubleshooting steps`}</h5>
    <Checkbox mdxType="Checkbox">
      <p>{`Ask the end user to temporarily disable ad-blockers or other third-party browser extensions that may be interfering with Link and try again.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`If the Item is at an institution that uses OAuth, see `}<a parentName="p" {...{
          "href": "#oauth-not-working"
        }}>{`OAuth not working`}</a>{`.`}</p>
    </Checkbox>
    <h4 {...{
      "id": "you-may-need-to-update-your-app-couldnt-connect-to-your-institution-or-something-went-wrong-an-internal-error-occurred-error-message"
    }}>{`"You may need to update your app", "Couldn't connect to your institution", or "Something went wrong: an internal error occurred" error message`}</h4>
    <h5 {...{
      "id": "symptom-5"
    }}>{`Symptom`}</h5>
    <p>{`After selecting a bank in Link, the user gets an error message saying "You may need to update your app in order to connect to Chase" (or another institution), "Couldn't connect to your institution", or "Something went wrong: an internal error occurred".`}</p>
    <h5 {...{
      "id": "common-causes-5"
    }}>{`Common causes`}</h5>
    <ul>
      <li parentName="ul">{`You haven't selected a use case for Link.`}</li>
      <li parentName="ul">{`The full list of OAuth prerequisites has not been completed to enable your client ID for a given institution's OAuth connection.`}</li>
      <li parentName="ul">{`A backend error occurred during the Link flow.`}</li>
    </ul>
    <h5 {...{
      "id": "troubleshooting-steps-5"
    }}>{`Troubleshooting steps`}</h5>
    <Checkbox mdxType="Checkbox">
      <p>{`Ensure you have selected use cases for Link, which you can do from the Dashboard, under `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/link/data-transparency-v5"
        }}>{`Customize -> Link -> Data Transparency`}</a>{`.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`See the troubleshooting guide for the `}<a parentName="p" {...{
          "href": "/docs/errors/institution/#unauthorized_institution"
        }}><inlineCode parentName="a">{`UNAUTHORIZED_INSTITUTION`}</inlineCode></a>{` error.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`The "something went wrong" message indicates a 500 error. Viewing the logs (for example, via `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/developers/logs"
        }}><strong parentName="a">{`Dashboard>Observe>Logs`}</strong></a>{`) will show you a more detailed error message and code.`}</p>
    </Checkbox>
    <h4 {...{
      "id": "interactive-brokers-customer-prompted-to-enter-a-query-id-and-token"
    }}>{`Interactive Brokers customer prompted to enter a query id and token`}</h4>
    <h5 {...{
      "id": "symptom-6"
    }}>{`Symptom`}</h5>
    <p>{`When linking an Interactive Brokers account, the end user is prompted to enter a "token" and/or "query id" but does not know where or how to get one.`}</p>
    <h5 {...{
      "id": "common-causes-6"
    }}>{`Common causes`}</h5>
    <p>{`This is part of the standard Interactive Brokers Link flow. Interactive Brokers requires this step for any customer linking a third-party service, such as Plaid, to their account.`}</p>
    <h5 {...{
      "id": "troubleshooting-steps-6"
    }}>{`Troubleshooting steps`}</h5>
    <Checkbox mdxType="Checkbox">
      <p>{`See the `}<a parentName="p" {...{
          "href": "https://www.interactivebrokers.com/lib/cstools/faq/#/content/41567214"
        }}>{`Interactive Brokers help center`}</a>{` for instructions on obtaining a query id and token. `}</p>
    </Checkbox>
    <h4 {...{
      "id": "oauth-not-working"
    }}>{`OAuth not working`}</h4>
    <p>{`First, verify these settings before reviewing the more detailed OAuth troubleshooting guides below:`}</p>
    <Checkbox mdxType="Checkbox">
      <p>{`Ensure your redirect URI is configured in the `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/developers/api"
        }}>{`Plaid Dashboard`}</a></p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`For web, iOS SDK, or React Native iOS integrations, ensure the `}<inlineCode parentName="p">{`redirect_uri`}</inlineCode>{` parameter is set in the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` call for all Link sessions, including Link in update mode.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`For Android SDK integrations, ensure that your Android package name is registered in the `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/developers/api"
        }}>{`Plaid Dashboard`}</a>{` and that the `}<inlineCode parentName="p">{`android_package_name`}</inlineCode>{` parameter is set in the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` call for all Link sessions, including Link in update mode.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`For iOS SDK integrations, check that the redirect URI has been associated with an Apple App Association file and that your application has the associated-domains entitlement for that URI. For more details, see the `}<a parentName="p" {...{
          "href": "/docs/link/oauth/#ios"
        }}>{`OAuth guide`}</a>{`.`}</p>
    </Checkbox>
    <p>{`If none of the steps above resolve your problem, see the specific OAuth troubleshooting guides below. If you do not see your problem listed, or following the guide does not fix the problem, `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/support/new/product-and-development/product-troubleshooting/sdk"
      }}>{`contact support`}</a>{`.`}</p>
    <h4 {...{
      "id": "oauth-institutions-not-appearing-in-link"
    }}>{`OAuth institutions not appearing in Link`}</h4>
    <h5 {...{
      "id": "symptom-7"
    }}>{`Symptom`}</h5>
    <p>{`Institutions that use OAuth, such as Capital One or Wells Fargo, are not appearing in the Link UI.`}</p>
    <h5 {...{
      "id": "common-causes-7"
    }}>{`Common causes`}</h5>
    <ul>
      <li parentName="ul">{`A `}<inlineCode parentName="li">{`redirect_uri`}</inlineCode>{` or `}<inlineCode parentName="li">{`android_package_name`}</inlineCode>{` was not specified in the call to `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`The redirect URI or Android package name was not allowlisted on the `}<a parentName="li" {...{
          "href": "https://dashboard.plaid.com/developers/api"
        }}>{`Plaid Dashboard`}</a>{`.`}</li>
    </ul>
    <h5 {...{
      "id": "troubleshooting-steps-7"
    }}>{`Troubleshooting steps`}</h5>
    <Checkbox mdxType="Checkbox">
      <p>{`Make sure the `}<inlineCode parentName="p">{`redirect_uri`}</inlineCode>{` (or, for Android, `}<inlineCode parentName="p">{`android_package_name`}</inlineCode>{`) is specified in the call to `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Make sure to list your redirect URI or Android package name in the Plaid Dashboard. This setting can be found under `}<a parentName="p" {...{
          "href": "https://dashboard.plaid.com/developers/api"
        }}><strong parentName="a">{`Build -> API -> Allowed Redirect URIs`}</strong>{` (or `}<strong parentName="a">{`Allowed Android package names`}</strong>{`) -> `}<strong parentName="a">{`Configure`}</strong></a>{`. `}</p>
    </Checkbox>
    <h4 {...{
      "id": "oauth-flow-shows-unsupported-browser-warning"
    }}>{`OAuth flow shows unsupported browser warning`}</h4>
    <h5 {...{
      "id": "symptom-8"
    }}>{`Symptom`}</h5>
    <p>{`Upon entering their institution's OAuth flow, users see a warning that their browser is not supported.`}</p>
    <h5 {...{
      "id": "common-causes-8"
    }}>{`Common causes`}</h5>
    <ul>
      <li parentName="ul">{`The Plaid integration is using a webview with an incorrectly configured user agent.`}</li>
    </ul>
    <h5 {...{
      "id": "troubleshooting-steps-8"
    }}>{`Troubleshooting steps`}</h5>
    <Checkbox mdxType="Checkbox">
      <p>{`Use an official Plaid mobile SDK or Hosted Link rather than a webview-based integration. `}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Ensure that the webview uses a valid user agent matching one commonly found in the wild.`}</p>
    </Checkbox>
    <h4 {...{
      "id": "oauth-redirects-not-working"
    }}>{`OAuth redirects not working`}</h4>
    <h5 {...{
      "id": "symptom-9"
    }}>{`Symptom`}</h5>
    <p>{`Users are not being redirected back to Link after completing their institution's OAuth flow.`}</p>
    <h5 {...{
      "id": "common-causes-9"
    }}>{`Common causes`}</h5>
    <h6 {...{
      "id": "android"
    }}>{`Android`}</h6>
    <ul>
      <li parentName="ul">{`An `}<inlineCode parentName="li">{`android_package_name`}</inlineCode>{` was not specified in the call to `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`An incorrect `}<inlineCode parentName="li">{`android_package_name`}</inlineCode>{` was specified in the call to `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{`.`}</li>
    </ul>
    <h6 {...{
      "id": "ios"
    }}>{`iOS`}</h6>
    <ul>
      <li parentName="ul">{`Universal Links are not correctly configured.`}</li>
    </ul>
    <h5 {...{
      "id": "troubleshooting-steps-9"
    }}>{`Troubleshooting steps`}</h5>
    <h6 {...{
      "id": "android-1"
    }}>{`Android`}</h6>
    <Checkbox mdxType="Checkbox">
      <p>{`Make sure the `}<inlineCode parentName="p">{`android_package_name`}</inlineCode>{` is correctly specified in the call to `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`.`}</p>
    </Checkbox>
    <h6 {...{
      "id": "ios-1"
    }}>{`iOS`}</h6>
    <Checkbox mdxType="Checkbox">
      <p>{`Ensure you are testing on a physical device and not an iOS simulator. Universal Links may not always work correctly on the simulator.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox"> 
      <p>{`Visit your redirect URI manually and confirm that it opens your app. You can do this by either clicking on this URI in a link (for instance, by adding it to the Reminders app) or by using the command line tool to open the URL in your currently running simulator: `}<inlineCode parentName="p">{`xcrun simctl openurl booted '<your URI>'`}</inlineCode>{`.`}</p>
      <p>{`If this does successfully open your app, confirm that the `}<inlineCode parentName="p">{`redirect_uri`}</inlineCode>{` is correctly set in your `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` call. If it does not, there is likely a problem with the `}<inlineCode parentName="p">{`apple-app-site-association`}</inlineCode>{` file or the entitlements for the app; view the troubleshooting steps below.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Test the `}<inlineCode parentName="p">{`apple-app-site-association`}</inlineCode>{` file by taking the domain (not the full path) of the `}<inlineCode parentName="p">{`redirect_uri`}</inlineCode>{` and accessing
the corresponding location on Apple's CDN using a web browser. For example, if the `}<inlineCode parentName="p">{`redirect_uri`}</inlineCode>{` is `}<inlineCode parentName="p">{`https://app.wonderwallet.com/plaid`}</inlineCode>{`, then visit `}<inlineCode parentName="p">{`https://app-site-association.cdn-apple.com/a/v1/app.wonderwallet.com`}</inlineCode>{`. `}</p>
      <p>{`If the contents of the `}<inlineCode parentName="p">{`apple-app-site-association`}</inlineCode>{` blob do not appear, then Apple does not have a copy of the `}<inlineCode parentName="p">{`apple-app-site-association`}</inlineCode>{` file at the correct location needed to invoke Universal Links. See `}<a parentName="p" {...{
          "href": "https://developer.apple.com/documentation/xcode/allowing-apps-and-websites-to-link-to-your-content"
        }}>{`Apple's Universal Links documentation`}</a>{` for information on how to create, name, and locate this file.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Confirm that the `}<inlineCode parentName="p">{`apple-app-site-association`}</inlineCode>{` contains the exact and correct path for the `}<inlineCode parentName="p">{`redirect_uri`}</inlineCode>{`.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Confirm that the `}<inlineCode parentName="p">{`apple-app-site-association`}</inlineCode>{` is not malformatted, using a tool such as the `}<a parentName="p" {...{
          "href": "https://branch.io/resources/aasa-validator/"
        }}>{`ASAA validator`}</a>{`. If combining iOS 12 style (`}<inlineCode parentName="p">{`paths:`}</inlineCode>{`) and iOS 13 or higher style (`}<inlineCode parentName="p">{`components:`}</inlineCode>{`) formats, the two should be in separate sections of the file, as in this `}<a parentName="p" {...{
          "href": "https://cdn-testing.plaid.com/.well-known/apple-app-site-association"
        }}>{`example app site association file`}</a>{`.`}</p>
    </Checkbox>
    <Checkbox mdxType="Checkbox">
      <p>{`Ensure that the associated domains have been added as entitlements to your app. For instructions, see `}<a parentName="p" {...{
          "href": "https://developer.apple.com/documentation/xcode/allowing-apps-and-websites-to-link-to-your-content"
        }}>{`Apple's Universal Links documentation`}</a>{`.`}</p>
    </Checkbox>
    <h4 {...{
      "id": "current-version-not-supported-or-public_key_not_enabled"
    }}>{`"Current version not supported" or `}<inlineCode parentName="h4">{`PUBLIC_KEY_NOT_ENABLED`}</inlineCode></h4>
    <h5 {...{
      "id": "symptom-10"
    }}>{`Symptom`}</h5>
    <ul>
      <li parentName="ul">{`Link displays a "Current version not supported" message on launch, or you see a `}<inlineCode parentName="li">{`PUBLIC_KEY_NOT_ENABLED`}</inlineCode>{` error on the backend.`}</li>
    </ul>
    <h5 {...{
      "id": "common-causes-10"
    }}>{`Common causes`}</h5>
    <ul>
      <li parentName="ul">{`The integration is using a public key to launch Link instead of a Link token. As of February 2025, public key integrations are no longer allowed.`}</li>
    </ul>
    <h5 {...{
      "id": "troubleshooting-steps-10"
    }}>{`Troubleshooting steps`}</h5>
    <Checkbox mdxType="Checkbox">
      <p>{`See the `}<a parentName="p" {...{
          "href": "/docs/link/link-token-migration-guide/"
        }}>{`Link token migration guide`}</a>{` to migrate to Link tokens.`}</p>
    </Checkbox>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "link-error-codes",
        level: 4,
        title: "Link error codes",
      },
{
        id: "missing-institutions-or-connectivity-not-supported-error",
        level: 4,
        title: "Missing institutions or \"Connectivity not supported\" error",
      },
{
        id: "institution-unhealthy",
        level: 4,
        title: "Institution unhealthy",
      },
{
        id: "duplicate-items",
        level: 4,
        title: "Duplicate Items",
      },
{
        id: "missing-accounts",
        level: 4,
        title: "Missing accounts",
      },
{
        id: "link-flow-is-failing",
        level: 4,
        title: "Link flow is failing",
      },
{
        id: "you-may-need-to-update-your-app-couldnt-connect-to-your-institution-or-something-went-wrong-an-internal-error-occurred-error-message",
        level: 4,
        title: "\"You may need to update your app\", \"Couldn't connect to your institution\", or \"Something went wrong: an internal error occurred\" error message",
      },
{
        id: "interactive-brokers-customer-prompted-to-enter-a-query-id-and-token",
        level: 4,
        title: "Interactive Brokers customer prompted to enter a query id and token",
      },
{
        id: "oauth-not-working",
        level: 4,
        title: "OAuth not working",
      },
{
        id: "oauth-institutions-not-appearing-in-link",
        level: 4,
        title: "OAuth institutions not appearing in Link",
      },
{
        id: "oauth-flow-shows-unsupported-browser-warning",
        level: 4,
        title: "OAuth flow shows unsupported browser warning",
      },
{
        id: "oauth-redirects-not-working",
        level: 4,
        title: "OAuth redirects not working",
      },
{
        id: "current-version-not-supported-or-public_key_not_enabled",
        level: 4,
        title: "\"Current version not supported\" or PUBLIC_KEY_NOT_ENABLED",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
